/** @format */

import React, { useCallback, useEffect, useRef, useState } from "react";
import Draggable, {
  DraggableData,
  DraggableEvent,
  DraggableEventHandler,
} from "react-draggable";
import { ReactComponent as CardImage } from "bootstrap-icons/icons/card-image.svg";
import { Image } from "../resources/types";
import { produce } from "immer";
import { useTranslation } from "react-i18next";

type Props = {
  onChangeImage: (image: Image) => void;
  image: Image;
};

const HeroImage: React.FC<Props> = ({ onChangeImage, image }) => {
  const { t: _ } = useTranslation();
  const [sliderHeight, setSliderHeight] = useState<number>(80);
  const sliderContainerHeight: number = 200 - 0 * 2;
  const [position, setPosition] = useState<number>(sliderContainerHeight / 2);

  const isValid = Boolean(!image.src || image.src.startsWith("https://"));

  const lessonBannerRef = useRef<HTMLDivElement>(null);
  const draggableRef = useRef(null);
  const posPx =
    ((image.$metadata?.positionY ?? 50) / 100) *
      (sliderContainerHeight - sliderHeight) +
    sliderHeight / 2;

  const handleDrag: DraggableEventHandler = useCallback(
    (e: DraggableEvent, ui: DraggableData) => {
      if (!(ui.deltaY < 0 && position <= sliderHeight / 2))
        setPosition(position + ui.deltaY);
    },
    [position, setPosition, sliderHeight],
  );

  useEffect(() => {
    setPosition(
      Math.round(
        ((image.$metadata?.positionY ?? 50) / 100) *
          (sliderContainerHeight - sliderHeight) +
          sliderHeight / 2,
      ),
    );
  }, [sliderHeight, image.$metadata?.positionY, sliderContainerHeight]);

  const onResize = useCallback(
    () =>
      setSliderHeight(() => {
        let _sliderHeight: number = 80;
        if (lessonBannerRef.current && image.$metadata?.intrinsicSize) {
          _sliderHeight =
            (sliderContainerHeight /
              ((lessonBannerRef.current.offsetWidth /
                image.$metadata.intrinsicSize.width) *
                image.$metadata.intrinsicSize.height)) *
            sliderContainerHeight;
        }
        if (_sliderHeight > sliderContainerHeight)
          _sliderHeight = sliderContainerHeight;
        return _sliderHeight;
      }),
    [image.$metadata?.intrinsicSize, sliderContainerHeight],
  );

  useEffect(() => {
    if (lessonBannerRef.current) {
      onResize();
    }
  }, [image.src, onResize]);

  useEffect(() => {
    if (lessonBannerRef.current) {
      window.addEventListener("resize", onResize);
      return () => {
        window.removeEventListener("resize", onResize);
      };
    }
  }, [onResize, setSliderHeight, sliderHeight]);

  return (
    <div className="mb-3">
      <div className="d-flex gap-2">
        <div
          ref={lessonBannerRef}
          className="flex-grow-1 position-relative d-flex justify-content-center align-items-center text-center"
          style={{ height: "200px", color: "var(--bs-border-color)" }}
        >
          <div className="my-auto">
            <CardImage style={{ width: "40px", height: "40px" }} />
            <p className="m-0">
              {image.src
                ? isValid
                  ? `${_("Loading")}...`
                  : `${_("Invalid image URL")}`
                : `${_("No image selected")}`}
            </p>
          </div>
          <div
            className="rounded position-absolute w-100 h-100 top-0"
            style={{
              backgroundImage: `url('${image.src}${
                image.src.includes("images.unsplash.com")
                  ? "&fm=jpg&w=1200"
                  : ""
              }')`,
              backgroundPosition: `center ${
                ((position - sliderHeight / 2) /
                  (sliderContainerHeight - sliderHeight)) *
                100
              }%`,
              backgroundSize: "cover",
            }}
          ></div>
        </div>
        <div className="d-flex bg-black rounded">
          <div
            className="d-flex position-relative p-0 rounded"
            style={{
              background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), center / cover url('${
                image.src
              }${
                image.src.includes("images.unsplash.com")
                  ? "&fm=jpg&h=200&w=60&fit=crop&q=1"
                  : ""
              }')`,
            }}
          >
            <Draggable
              nodeRef={draggableRef}
              onStop={() =>
                onChangeImage(
                  produce(image, (draft) => {
                    const pos = Math.round(
                      ((position - sliderHeight / 2) /
                        (sliderContainerHeight - sliderHeight)) *
                        100,
                    );

                    let _pos = isNaN(pos) ? 0 : pos;
                    if (_pos > 100) _pos = 100;
                    if (_pos < 0) _pos = 0;

                    draft.$metadata = { ...draft.$metadata, positionY: _pos };
                  }),
                )
              }
              onDrag={handleDrag}
              axis="y"
              bounds="parent"
              position={{ x: 0, y: posPx - sliderHeight / 2 }}
            >
              <div
                ref={draggableRef}
                data-testid={`draggable-slider`}
                className="bg-white opacity-50"
                style={{
                  cursor: "ns-resize",
                  width: "60px",
                  height: `${sliderHeight}px`,
                }}
              ></div>
            </Draggable>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroImage;
